import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem("rk_token")) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      setTimeout(() => {
        toast.dismiss();
        toast.error("Sesi berakhir atau akun dinonaktifkan. Silakan login ulang.", { id: "session-expired" });
      }, 60);
    };
    const onPerms = () => {
      refresh();
    };
    window.addEventListener("rk-session-expired", onExpired);
    window.addEventListener("rk-perms-changed", onPerms);
    window.addEventListener("focus", onPerms);
    return () => {
      window.removeEventListener("rk-session-expired", onExpired);
      window.removeEventListener("rk-perms-changed", onPerms);
      window.removeEventListener("focus", onPerms);
    };
  }, [refresh]);

  const login = async (username, password) => {
    const { data } = await api.post("/auth/login", { username, password });
    localStorage.setItem("rk_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const setToken = (token) => localStorage.setItem("rk_token", token);

  const logout = useCallback(() => {
    localStorage.removeItem("rk_token");
    setUser(null);
  }, []);

  const can = (module, action = "view") => Boolean(user?.effective_permissions?.[module]?.[action]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, setToken, can }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
